import { RLayer, RSource, useMap } from "maplibre-react-components";
import React, { useEffect, useMemo } from "react";
import type {
  FillLayerSpecification,
  DataDrivenPropertyValueSpecification,
  Map as MapLibreMap,
  MapLayerMouseEvent,
} from "maplibre-gl";
import { makeConditionalStepExpression } from "@/utils/helpers";
import { useTariffLayer } from "@/store/mode";
import { useSubscriptionStore } from "@/store/useSubscriptionStore";
import { DEFAULT_SOURCE_TABLE } from "@/store/AdminToolsProvider";
import { useRefreshTick } from "@/utils/hooks/useRefreshTick";
import { isEdaTariffMode, tariffModeToLayer } from "@/types/base";
import { usePriceRangesDataContext } from "@/store/PriceRangesProvider";
import { useSelectedHexGroupStore } from "@/store/selectedHexGroup";
import { useAgglomerationsDataContext } from "@/store/AgglomerationsProvider";
import {
  buildHexFillOverlayFilter,
  buildHexgroupIdFilter,
  getActiveEdaHexgroupIds,
} from "@/utils/mapFilters";
import { useSnapshotsStore } from "@/store/snapshots";
import { getTariffModeConfig } from "@/utils/tariffModeConfig";

const OPACITY_EXPR: DataDrivenPropertyValueSpecification<number> = [
  "interpolate",
  ["linear"],
  ["zoom"],
  7,
  0.85,
  10,
  0.65,
  14,
  0.35,
];
const templateStops = [50, 100, 200, 500, 1000, 1500];

const DEFAULT_COLOR = "#000";

const CoefficientLayer = () => {
  const map = useMap() as MapLibreMap | null;
  const refreshTick = useRefreshTick();

  const selectedHexGroup = useSelectedHexGroupStore((s) => s.selectedHexGroup);

  const { isSubscriptionActive } = useSubscriptionStore();

  const tariffLayer = useTariffLayer((s) => s.tariffLayer);
  const activeSnapshotTimestamp = useSnapshotsStore(
    (s) => s.activeSnapshotTimestamp
  );
  const isEdaMode = isEdaTariffMode(tariffLayer);
  const tariffConfig = getTariffModeConfig(tariffLayer);
  const { agglomerations } = useAgglomerationsDataContext();

  const { priceRanges } = usePriceRangesDataContext();
  const activeEdaHexgroupIds = useMemo(
    () => getActiveEdaHexgroupIds(agglomerations),
    [agglomerations]
  );
  const activeEdaFilter = useMemo(
    () => buildHexgroupIdFilter(activeEdaHexgroupIds),
    [activeEdaHexgroupIds]
  );
  const priceRangesMap = useMemo(() => {
    if (!priceRanges) return {};
    const m: Record<number, { min: number; max: number }> = {};
    priceRanges.forEach((p) => {
      const range = p.ranges[tariffLayer];
      if (range && range.min != null && range.max != null) {
        m[p.id] = {
          min: tariffConfig.zeroBasedRange ? 0 : range.min,
          max: range.max,
        };
      } else {
        m[p.id] = { min: 100, max: 800 };
      }
    });
    return m;
  }, [priceRanges, tariffConfig.zeroBasedRange, tariffLayer]);

  const effectiveSnapshotTimestamp = tariffConfig.useSnapshots
    ? activeSnapshotTimestamp
    : null;

  const tilesUrl = useMemo(() => {
    const base = effectiveSnapshotTimestamp
      ? `${__MARTIN_URL__}/rpc/public.hexagon_snapshot_tiles/{z}/{x}/{y}.pbf`
      : `${__MARTIN_URL__}/${DEFAULT_SOURCE_TABLE}/{z}/{x}/{y}.pbf`;
    const params = new URLSearchParams({ rt: String(refreshTick) });

    if (effectiveSnapshotTimestamp) {
      params.set("snapshot_timestamp", effectiveSnapshotTimestamp);
    }

    return [`${base}?${params.toString()}`];
  }, [effectiveSnapshotTimestamp, refreshTick]);

  const sourceLayer = effectiveSnapshotTimestamp
    ? "hexagon_snapshot_tiles"
    : DEFAULT_SOURCE_TABLE;

  useEffect(() => {
    if (!map) return;

    const nextFilter = buildHexFillOverlayFilter({
      isEdaMode,
      activeEdaFilter,
      selectedHexGroupId: selectedHexGroup?.id,
    });

    if (map.getLayer("hex-fill-overlay")) {
      map.setFilter("hex-fill-overlay", nextFilter);
    }
  }, [activeEdaFilter, isEdaMode, map, selectedHexGroup?.id, tariffLayer]);

  const hexFill = useMemo<FillLayerSpecification>(() => {
    const exp: FillLayerSpecification = {
      id: "hex-fill",
      type: "fill",
      source: "hex-src",
      "source-layer": sourceLayer,
      minzoom: 5,
      ...(isEdaMode ? { filter: activeEdaFilter } : {}),
      paint: {
        "fill-color": makeConditionalStepExpression(
          tariffModeToLayer[tariffLayer],
          priceRangesMap,
          tariffConfig.colorScale,
          templateStops,
          "relative",
          DEFAULT_COLOR
        ),
        "fill-opacity": OPACITY_EXPR,
      },
    };

    return exp;
  }, [
    activeEdaFilter,
    isEdaMode,
    priceRangesMap,
    sourceLayer,
    tariffConfig.colorScale,
    tariffLayer,
  ]);

  const hexFillOverlay = useMemo<FillLayerSpecification>(
    () => ({
      id: "hex-fill-overlay",
      type: "fill",
      source: "hex-src",
      "source-layer": sourceLayer,
      filter: buildHexFillOverlayFilter({
        isEdaMode,
        activeEdaFilter,
        selectedHexGroupId: selectedHexGroup?.id,
      }),
      paint: {
        "fill-color": "#fff",
        "fill-opacity": 0.72,
      },
    }),
    [activeEdaFilter, isEdaMode, selectedHexGroup?.id, sourceLayer, tariffLayer]
  );

  function handleHexClick(e: MapLayerMouseEvent) {
    const feat = e.features?.[0];
    if (!feat) return;
    console.log(feat.properties);
  }

  if (!isSubscriptionActive) return null;

  return (
    <React.Fragment key={sourceLayer}>
      <RSource
        key={`hex-src-${sourceLayer}`}
        id="hex-src"
        type="vector"
        tiles={tilesUrl}
        minzoom={0}
        maxzoom={14}
      />
      <RLayer
        key={`hex-fill-${sourceLayer}`}
        {...hexFill}
        onClick={handleHexClick}
      />
      <RLayer
        key={`hex-fill-overlay-${sourceLayer}`}
        {...hexFillOverlay}
        beforeId="hex-border"
      />
    </React.Fragment>
  );
};

export default CoefficientLayer;
